import { useId } from 'react';
import { WorkspaceIcon } from '../icons';
import { CopyLine } from './copy-line';
import {
  dateTimeLabel,
  dayLabel,
  grantState,
  lastUsedLabel,
  methodLine,
  stateLabel,
  type GrantState,
} from './format';
import { FamilyRow } from './family-row';
import type { AgentAccessScopeInfo, ApprovalMode, KnowledgeClientGrant } from './types';
import connectionsCopy, { type ConnectionsCopy } from '@/i18n/ns/connections';
import agentPermissionsCopy, { type AgentPermissionsCopy } from '@/i18n/ns/agent-permissions';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';

const SCOPE_ORDER = [
  'knowledge:read',
  'knowledge:propose',
  'library:read',
  'stores:read',
  'media:read',
  'library:write',
  'data:connections',
];
const PERMISSIONS_EN: AgentPermissionsCopy = agentPermissionsCopy.en;

/** Access beyond reading that a person can switch on or off per agent. */
export interface OptionalScope {
  id: string;
  title: string;
  describe: (app: string) => string;
  confirmOn: (app: string, company: string) => string;
  confirmOff: (app: string) => string;
  enabled: (app: string) => string;
  disabled: (app: string) => string;
  /** Scopes that switch on with this one: it does nothing without them. */
  needs?: string[];
  /** Scopes that switch off with this one: they do nothing without it. */
  carries?: string[];
  /** Offered only when the server lists it for this company (Library access follows its rollout). */
  listedOnly?: boolean;
  /** Only for an agent that signed in: an access key holds company knowledge reading alone. */
  signedInOnly?: boolean;
}

type ScopeCopy = { title: string } & Record<
  'describe' | 'confirmOn' | 'confirmOff' | 'enabled' | 'disabled',
  string
>;
const switchCopy = (
  id: string,
  copy: ScopeCopy,
  extra: Partial<OptionalScope> = {},
): OptionalScope => ({
  id,
  title: copy.title,
  describe: (app) => formatCopy(copy.describe, { app }),
  confirmOn: (app, company) => formatCopy(copy.confirmOn, { app, company }),
  confirmOff: (app) => formatCopy(copy.confirmOff, { app }),
  enabled: (app) => formatCopy(copy.enabled, { app }),
  disabled: (app) => formatCopy(copy.disabled, { app }),
  ...extra,
});

/** The optional scopes this page has a switch for, in the interface language. */
export function scopeOptions(
  copy: ConnectionsCopy,
  permissions: AgentPermissionsCopy = PERMISSIONS_EN,
): OptionalScope[] {
  return [
    switchCopy('knowledge:propose', {
      title: copy.scopeProposeTitle,
      describe: copy.scopeProposeDescribe,
      confirmOn: copy.scopeProposeConfirmOn,
      confirmOff: copy.scopeProposeConfirmOff,
      enabled: copy.scopeProposeEnabled,
      disabled: copy.scopeProposeDisabled,
    }),
    // Every Library tool needs read; saving and asking also need write.
    switchCopy(
      'library:read',
      {
        title: copy.scopeLibraryReadTitle,
        describe: copy.scopeLibraryReadDescribe,
        confirmOn: copy.scopeLibraryReadConfirmOn,
        confirmOff: copy.scopeLibraryReadConfirmOff,
        enabled: copy.scopeLibraryReadEnabled,
        disabled: copy.scopeLibraryReadDisabled,
      },
      { carries: ['library:write', 'data:connections'], listedOnly: true, signedInOnly: true },
    ),
    // Live stores and media monitoring are reads of their own, apart from the Library.
    switchCopy(
      'stores:read',
      {
        title: permissions.storesTitle,
        describe: permissions.storesDescribe,
        confirmOn: permissions.storesConfirmOn,
        confirmOff: permissions.storesConfirmOff,
        enabled: permissions.storesEnabled,
        disabled: permissions.storesDisabled,
      },
      { listedOnly: true, signedInOnly: true },
    ),
    switchCopy(
      'media:read',
      {
        title: permissions.mediaTitle,
        describe: permissions.mediaDescribe,
        confirmOn: permissions.mediaConfirmOn,
        confirmOff: permissions.mediaConfirmOff,
        enabled: permissions.mediaEnabled,
        disabled: permissions.mediaDisabled,
      },
      { listedOnly: true, signedInOnly: true },
    ),
    switchCopy(
      'library:write',
      {
        title: copy.scopeLibraryWriteTitle,
        describe: copy.scopeLibraryWriteDescribe,
        confirmOn: copy.scopeLibraryWriteConfirmOn,
        confirmOff: copy.scopeLibraryWriteConfirmOff,
        enabled: copy.scopeLibraryWriteEnabled,
        disabled: copy.scopeLibraryWriteDisabled,
      },
      { needs: ['library:read'], listedOnly: true, signedInOnly: true },
    ),
    switchCopy(
      'data:connections',
      {
        title: permissions.connTitle,
        describe: permissions.connDescribe,
        confirmOn: permissions.connConfirmOn,
        confirmOff: permissions.connConfirmOff,
        enabled: permissions.connEnabled,
        disabled: permissions.connDisabled,
      },
      { needs: ['library:read'], listedOnly: true, signedInOnly: true },
    ),
  ];
}

/** The switches a grant shows: some only for an agent that signed in. */
export const grantSwitches = (grant: KnowledgeClientGrant, offered: OptionalScope[]) =>
  offered.filter((option) => !option.signedInOnly || grant.kind !== 'access_key');

/** The full scope set a grant should hold after one optional scope changes. */
export function nextScopes(grant: KnowledgeClientGrant, option: OptionalScope, enable: boolean) {
  const changed = new Set([option.id, ...((enable ? option.needs : option.carries) ?? [])]);
  // Scopes this page does not know about are kept as they are.
  return [
    ...SCOPE_ORDER.filter(
      (id) => id === 'knowledge:read' || (changed.has(id) ? enable : grant.scopes.includes(id)),
    ),
    ...grant.scopes.filter((id) => !SCOPE_ORDER.includes(id)),
  ];
}

/**
 * What the grant allows beyond the switches shown under it: reading, in the
 * server's own consent words when it sent them, and any scope without a switch.
 */
function accessLines(
  grant: KnowledgeClientGrant,
  described: AgentAccessScopeInfo[],
  switched: string[],
  copy: ConnectionsCopy,
  permissions: AgentPermissionsCopy,
) {
  const ordered = [
    ...SCOPE_ORDER.filter((id) => grant.scopes.includes(id)),
    ...grant.scopes.filter((id) => !SCOPE_ORDER.includes(id)),
  ].filter((id) => !switched.includes(id));
  return ordered.map((id) => {
    const scope = described.find((entry) => entry.id === id);
    if (id === 'knowledge:read')
      return scope?.description ? `${scope.label}: ${scope.description}` : copy.agentReadAccess;
    return (
      scopeOptions(copy, permissions).find((option) => option.id === id)?.title ??
      scope?.label ??
      id
    );
  });
}

/** A letter tile for an agent: the first letter of the name it reported. */
function Monogram({ name }: { name: string }) {
  const letter = Array.from(name.trim())[0]?.toLocaleUpperCase() ?? '?';
  return (
    <span className="ws-agent-avatar" aria-hidden="true">
      {letter}
    </span>
  );
}

const TONE: Record<GrantState, string> = { ready: 'ok', waiting: 'waiting', stale: 'stale' };

/** The short state, then when it was last used, as one readable phrase. */
function StatusPill({ grant, now }: { grant: KnowledgeClientGrant; now: number }) {
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  const used = lastUsedLabel(grant, now, copy, locale);
  return (
    <span className="ws-agent-status" data-tone={TONE[grantState(grant)]}>
      <span className="ws-agent-pill">{stateLabel(grant, copy)}</span>
      {used && (
        <span
          className="ws-agent-when"
          title={dateTimeLabel(grant.lastUsedAt ?? grant.firstReadAt, copy, locale)}
        >
          <span className="ws-sr-only">· </span>
          {used}
        </span>
      )}
    </span>
  );
}

/** What finishes or repairs the connection, under the agent's name. */
function RowHint({ grant, showSample }: { grant: KnowledgeClientGrant; showSample: boolean }) {
  const copy = useNamespaceCopy(connectionsCopy);
  const app = grant.clientName;
  const state = grantState(grant);
  const key = grant.kind === 'access_key';
  if (state === 'ready') return null;
  if (state === 'stale')
    return (
      <div className="ws-agent-card-hint" data-tone="stale">
        <p>{key ? copy.agentHintKeyStale : formatCopy(copy.agentHintStale, { app })}</p>
      </div>
    );
  return (
    <div className="ws-agent-card-hint">
      <p>
        {key ? copy.agentHintKeyWaiting : formatCopy(copy.agentHintWaiting, { app })}
        {showSample && ` ${copy.agentHintTryAsking}`}
      </p>
      {/* The setup's own "Try it" step holds the same question when it is open. */}
      {showSample && (
        <CopyLine
          value={copy.setupSampleQuestion}
          label={formatCopy(copy.copyWhatSampleFor, { app })}
          kind="text"
        />
      )}
    </div>
  );
}

/** A date to read, with its exact time on hover. */
function When({ value }: { value: string }) {
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  return <span title={dateTimeLabel(value, copy, locale)}>{dayLabel(value, copy, locale)}</span>;
}

export function GrantRow({
  grant,
  open,
  busy,
  working,
  now,
  offered,
  described,
  showSample,
  onToggle,
  onScope,
  onMode,
  onNotice,
  onApprovalChanged,
  onDisconnect,
}: {
  grant: KnowledgeClientGrant;
  open: boolean;
  /** Another change is in flight: actions wait for it. */
  busy: boolean;
  /** This grant's change is in flight. */
  working: boolean;
  now: number;
  offered: OptionalScope[];
  /** The server's own description of each scope. */
  described: AgentAccessScopeInfo[];
  /** Whether a waiting row offers the sample question itself. */
  showSample: boolean;
  onToggle: () => void;
  onScope: (scope: OptionalScope, enable: boolean) => void;
  /** Sets how a family with modes approves: ask each time, or automatic. */
  onMode: (family: string, mode: ApprovalMode) => void;
  onNotice: (text: string, tone: 'ok' | 'error') => void;
  /** The grant after an allowed action changed. */
  onApprovalChanged: (client: KnowledgeClientGrant) => void;
  onDisconnect: () => void;
}) {
  const copy = useNamespaceCopy(connectionsCopy);
  const permissions = useNamespaceCopy(agentPermissionsCopy);
  const locale = useChatInterfaceLocale();
  const panel = useId();
  const key = grant.kind === 'access_key';
  // An agent on an old address can only be removed: its use and access no
  // longer matter, so the open row shows only when it began and ends.
  const live = grant.addressMatches;
  const switches = live ? grantSwitches(grant, offered) : [];
  const access = live
    ? accessLines(
        grant,
        described,
        switches.map((scope) => scope.id),
        copy,
        permissions,
      )
    : [];
  return (
    <li
      className={`ws-agent-card${open ? ' is-open' : ''}`}
      data-status={grantState(grant)}
      data-grant={grant.grantId}
    >
      <button
        type="button"
        className="ws-agent-card-main"
        aria-expanded={open}
        aria-controls={open ? panel : undefined}
        onClick={onToggle}
      >
        <Monogram name={grant.clientName} />
        <span className="ws-agent-card-title">
          <span className="ws-connector-name">{grant.clientName}</span>
          <span className="ws-agent-caption">{methodLine(grant, copy, locale)}</span>
        </span>
        <StatusPill grant={grant} now={now} />
        <WorkspaceIcon name="down" size={16} className="ws-agent-chevron" />
      </button>
      <RowHint grant={grant} showSample={showSample} />
      {open && (
        <div className="ws-agent-card-panel" id={panel}>
          <dl className="ws-agent-facts">
            <div>
              <dt>{key ? copy.agentFactCreated : copy.agentFactConnected}</dt>
              <dd>{grant.createdAt ? <When value={grant.createdAt} /> : copy.agentNotRecorded}</dd>
            </div>
            {grant.expiresAt && (
              <div>
                <dt>{key ? copy.agentFactExpires : copy.agentFactSignInEnds}</dt>
                <dd>
                  <When value={grant.expiresAt} />
                </dd>
              </div>
            )}
            {access.length > 0 && (
              <div className="ws-agent-facts-wide">
                <dt>{copy.agentFactAccess}</dt>
                <dd>
                  <ul className="ws-agent-access">
                    {access.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </dl>
          {!key && live && (
            <p className="ws-agent-card-note">
              {copy.agentNameNote}
              {grant.expiresAt && ` ${copy.agentSignInNote}`}
            </p>
          )}
          {switches.length > 0 && (
            <div className="ws-agent-toggles">
              {switches.map((scope) => {
                const on = grant.scopes.includes(scope.id);
                return (
                  <FamilyRow
                    key={scope.id}
                    grant={grant}
                    option={{
                      id: scope.id,
                      title: scope.title,
                      describe: scope.describe(grant.clientName),
                    }}
                    app={grant.clientName}
                    on={on}
                    busy={busy}
                    onChange={() => onScope(scope, !on)}
                    onMode={onMode}
                    onNotice={onNotice}
                    onChanged={onApprovalChanged}
                  />
                );
              })}
            </div>
          )}
          {live && Object.keys(grant.approval ?? {}).length > 0 && (
            <p className="ws-agent-card-note ws-agent-floor-note">
              {formatCopy(permissions.floorNote, { app: grant.clientName })}
            </p>
          )}
          <div className="ws-connector-actions">
            <button
              type="button"
              className="ws-text-button ws-text-button-quiet"
              aria-disabled={busy}
              onClick={() => {
                if (!busy) onDisconnect();
              }}
            >
              {working ? copy.agentWorking : key ? copy.agentRevokeKey : copy.agentDisconnect}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
